import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 5.4 visual preview at /preview/lesson/5.4
 * "The professional email: writing that becomes a record"
 * Vocab: Receipt.
 */

export default function LessonPreviewPage5_4() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromLastTime />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 5 · Taking the chain into the wild · Lesson 5.4
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The professional email: writing that becomes a record
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Receipt · Subject · Next action</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 subject-line drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Two emails about the same problem. One gets fixed. One gets forwarded.">
      <p className="mt-6 text-text/90">
        A student has a billing problem with their orthodontist&apos;s office.
        They are charged for an appointment they were not present for. They
        write two drafts of the email. They send one. The one they did not send
        is below.
      </p>

      <div className="mt-6 space-y-4">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Draft A · Sent · Got fixed in two replies
          </p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-muted">
            Subject
          </p>
          <p className="mt-1 text-text/90">
            Disputed charge on 9/14 invoice (account #4421)
          </p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-muted">
            Body
          </p>
          <p className="mt-1 text-text/90 italic">
            &ldquo;Hi Jenna, the September 14 invoice charges $185 for an
            adjustment visit at 4:30 p.m. that day. I was at school until 4:15
            and could not have made that appointment, and your front-desk text
            on the morning of 9/14 confirmed it was rescheduled to 9/21.
            Attaching the text. Please remove the 9/14 line and resend the
            invoice. Happy to talk if anything is unclear.&rdquo;
          </p>
        </div>
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            Draft B · Not sent · Would have been forwarded to the office manager
          </p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-muted">
            Subject
          </p>
          <p className="mt-1 text-text/90">A question about my bill</p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-wider text-muted">
            Body
          </p>
          <p className="mt-1 text-text/90 italic">
            &ldquo;Hi, I am very frustrated with my recent bill. I do not think
            the charges are accurate and I would appreciate someone looking
            into this for me as soon as possible. This has been an extremely
            disappointing experience and I hope it can be resolved.&rdquo;
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        Draft A reads like a receipt: dates, amounts, a quoted text, an
        attached file, and one clear next action. Draft B reads like a feeling.
        The office can act on Draft A in five minutes. Draft B gets forwarded
        upward because no one knows what to do with it.
      </p>
      <p className="mt-4 text-text/90">
        Professional email is the kind of writing that gets quoted back to you.
        Lesson 5.4 is about writing emails so that being quoted back is fine.
      </p>
    </Section>
  );
}

/* --- Section 2: From last time --- */
function FromLastTime() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · 5.3" title="Public posts earn the next thumb. Professional emails earn the next reply.">
      <p className="mt-6 text-text/90">
        Lesson 5.3 was about strangers scrolling. Lesson 5.4 is about someone
        whose inbox is full and whose calendar is full and who needs the next
        ten minutes back. Your email either gives them that or costs them more.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Define Audience in one sentence." />
        <ReviewItem n="02" q="Name the four hooks that earn the next sentence." />
        <ReviewItem n="03" q="What is honest scale and why does it matter on a public post?" />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · Email is a record" title="Write the email like it will be quoted later. Because it will.">
      <Subhead n={1} title="The vocab for this lesson" />
      <p className="mt-4 text-text/90">
        One word reframes how you draft a professional email. Without it, email
        gets written like a chat. With it, email gets written like a document
        that has to hold up.
      </p>
      <VocabCard
        term="Receipt"
        definition="An email written so that any reader, including readers who join the thread later, can know what was asked, what was sent, what is true, and what happens next. A receipt does not need to be defended later because everything that mattered is in the email."
        analogy="A grocery receipt does not argue. It lists. Date, items, prices, total, payment method. Anyone reading it knows what happened. A professional email works the same way: list the facts the reader needs, attach the proof, name the next action, and stop. Tone does the work of being human. The rest does the work of being correct."
        memoryHook="Receipt equals: facts, proof, next action, no extra weather."
        why="Professional emails get forwarded. They get pasted into HR notes. They get screenshot into group chats. A feelings-first email becomes evidence against you in five different ways. A receipt-style email becomes the answer."
      />

      <Subhead n={2} title="The four parts of a receipt email" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most professional emails are shorter than students think they should be.
        Four parts. In order.
      </p>
      <PartsGrid />

      <Subhead n={3} title="The subject line is half the email" className="mt-12" />
      <p className="mt-4 text-text/90">
        A reader decides whether to open your email based on the subject and
        the first ten words of the preview. A good subject line is what the
        email is actually about plus an identifier the reader can match to a
        record on their side. Bad subjects waste the half of the email that
        decides whether the rest gets read.
      </p>
      <div className="mt-6 space-y-3">
        {SUBJECT_PAIRS.map((p, i) => (
          <div key={i} className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr] rounded-lg border border-line bg-surface p-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-rose-300">Weak subject</p>
              <p className="mt-2 text-sm text-text/85 italic">{p.weak}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-emerald-300">Strong subject</p>
              <p className="mt-2 text-sm text-text/90">{p.strong}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted">Why</p>
              <p className="mt-2 text-sm text-text/80">{p.why}</p>
            </div>
          </div>
        ))}
      </div>

      <Subhead n={4} title="Where AI helps and where AI hurts" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is excellent at tone calibration on professional email. AI is bad at
        the parts of an email that determine whether the email accomplishes
        what you sent it for.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The quote-back test" className="mt-12" />
      <p className="mt-4 text-text/90">
        Read your email out loud. Imagine the recipient pasting any sentence
        into a thread with their boss tomorrow. Does it still hold up? If a
        sentence would be embarrassing or impossible to defend in that
        forward, cut it or rewrite it.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The check
        </p>
        <p className="mt-3 text-text/90">
          Every sentence in a professional email has to survive being quoted
          out of context. If the answer to &ldquo;could this sentence get me in
          trouble out of context&rdquo; is yes, the sentence is doing the wrong
          job. Email is for receipts, not for venting.
        </p>
      </div>
    </Section>
  );
}

const PARTS = [
  {
    n: '01',
    name: 'The hook line',
    body: 'One sentence at the top that names exactly what this email is about. Not "I hope you are doing well." Not "Following up." A sentence the reader could quote back to know what the email is for.',
    accent: 'amber' as const,
  },
  {
    n: '02',
    name: 'The facts',
    body: 'Dates, amounts, names, what was promised, what happened. Numbered if there is more than one. The reader should not have to dig in their own records to follow the email.',
    accent: 'emerald' as const,
  },
  {
    n: '03',
    name: 'The proof',
    body: 'Attachments, screenshots, links, quoted prior emails. Not "I have proof." The actual proof, attached to this message. If you make a claim, the evidence is in the same email.',
    accent: 'amber' as const,
  },
  {
    n: '04',
    name: 'The next action',
    body: 'One sentence. What you are asking them to do, by when, and what would unblock you. Not "please advise." A specific thing a specific person can do this week.',
    accent: 'emerald' as const,
  },
];

function PartsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {PARTS.map((p) => {
        const isEm = p.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={p.n} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">Part {p.n}</span>
              <h4 className={`text-lg font-semibold ${labelCls}`}>{p.name}</h4>
            </div>
            <p className="mt-3 text-text/90">{p.body}</p>
          </div>
        );
      })}
    </div>
  );
}

const SUBJECT_PAIRS = [
  {
    weak: 'Quick question',
    strong: 'Quick question on the 11/12 invoice (account #4421)',
    why: '“Quick question” sits in an inbox for a week. The strong version names what the question is about and gives the reader an account number to pull.',
  },
  {
    weak: 'Following up',
    strong: 'Following up on the 10/4 sign-off for the senior project',
    why: 'Following up on what? The strong version names the date and the deliverable, so the reader knows whether to open or schedule it.',
  },
  {
    weak: 'Help',
    strong: 'Locked out of the lab portal: cannot submit before 5pm deadline',
    why: '“Help” tells the reader nothing. The strong version names the problem, the consequence, and a time pressure.',
  },
];

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Tone: keeping the email calm when you are upset.',
      'Compression: turning four paragraphs into four sentences.',
      'Subject lines: generating five variations so you can pick the clearest.',
      'Hedging out emotional language while keeping the facts.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'The facts: AI will invent dates, names, and amounts that look plausible.',
      'The next action: AI defaults to "please advise" and "let me know."',
      'The proof: AI cannot attach the screenshot. You have to remember.',
      'The local context: AI does not know what the person you are writing has already done.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the subject that gets a reply" title="Three emails. Pick the subject line that gets opened first.">
      <p className="mt-6 text-text/90">
        Each drill shows two subject lines for the same email going to the same
        person. Pick the one that earns the open and frames the email as a
        receipt.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <SubjectDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  context: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    context:
      'Email to a teacher requesting an extension on a research paper after a documented illness. The teacher receives roughly 80 emails a day from students.',
    optionA: 'Extension request: AP US History paper due 11/14 (doctor note attached)',
    optionB: 'A question about my paper',
    correct: 'A',
    verdict:
      'Option A names the request, the class, the due date, and signals that the proof is already attached. The teacher knows whether to open this email now or at the end of the day. Option B could be about anything from any student. It sits unopened until tomorrow. The same email body works better behind Option A.',
  },
  {
    n: '02',
    context:
      'Cold email to a local engineering firm asking for a 20-minute conversation about summer internships. The recipient is a senior partner the writer has never met.',
    optionA: 'Hello',
    optionB:
      'Junior at Pearce HS asking for 20 minutes about your apprenticeship program',
    correct: 'B',
    verdict:
      'Option B tells the partner who is writing, what they want, and how long it will take, all before the email is opened. A senior partner deciding whether to respond can do so from the subject line alone. Option A makes the partner do work they do not have time to do. Most "Hello" subject lines go to spam or the bottom of the inbox.',
  },
  {
    n: '03',
    context:
      'Email to a hiring manager following up on a part-time job application submitted ten days ago. No response so far.',
    optionA: 'Just checking in',
    optionB:
      'Following up on my 10/22 application for the after-school tutor role',
    correct: 'B',
    verdict:
      'Option B reminds the hiring manager exactly what the email is about and exactly when the application came in, so the email becomes a useful record on their side too. The manager can pull the file in one click. Option A reads like a generic nudge and tells the manager nothing about why this email exists, which is the opposite of the job a follow-up subject line is supposed to do.',
  },
];

function SubjectDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The context
      </p>
      <p className="mt-3 text-text/90">{drill.context}</p>

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which subject line earns the open?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Subject {key}
                </span>
                <span className="mt-2 block">{text}</span>
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Subject {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That subject line does not earn the open. See the one that does
              below.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The subject that earns the open' : 'Correct'} · Subject {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Receipt in one sentence." answer="An email written so that any reader can know what was asked, what was sent, what is true, and what happens next, with no extra weather." />
        <QuickCheckItem n="Q2" q="The four parts of a receipt email, in order." answer="The hook line, the facts, the proof, the next action." />
        <QuickCheckItem n="Q3" q="Why is the subject line half the email?" answer="Because the reader decides whether to open at all based on the subject and the first ten words. A vague subject wastes the half of the email that determines whether the rest gets read." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on a professional email. Two things AI hurts." answer="Helps: tone, compression, subject-line variants, removing emotional language. Hurts: facts (AI invents), the next action (defaults to &ldquo;please advise&rdquo;), the proof (cannot attach), local context AI does not know." />
        <QuickCheckItem n="Q5" q="What is the quote-back test?" answer="Read your email out loud. Imagine the recipient pasting any sentence into a thread with their boss tomorrow. Anything that would not hold up out of context has to be cut or rewritten." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The receipt-email map.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 5.4 · Lock card · The professional email
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          Write every email so that being quoted back is fine.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Hook" body="One sentence at the top naming exactly what the email is about." />
          <LockTile n="02" name="Facts" body="Dates, amounts, names. Numbered if more than one. No detective work for the reader." />
          <LockTile n="03" name="Proof" body="Attachments, screenshots, quoted prior emails. The evidence ships with the claim." />
          <LockTile n="04" name="Next" body="One sentence. Specific action, specific person, specific timeline." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The subject test
          </p>
          <p className="mt-2 text-text/90">
            Subject is half the email. Name what the email is about plus an
            identifier the reader can match. Cut: &ldquo;Quick question,&rdquo;
            &ldquo;Following up,&rdquo; &ldquo;Hello,&rdquo; &ldquo;Help.&rdquo;
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The quote-back test
          </p>
          <p className="mt-2 text-text/90">
            Could any sentence get pasted into a thread with the reader&apos;s
            boss tomorrow? If a line would not hold up out of context, cut it
            or rewrite it. Email is for receipts, not for venting.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Tone is human. The rest is correct.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 5.5 · Week 5 capstone
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The chain is yours now: ship one real thing
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Pick a real artifact for a real reader. Run the chain, run the five
            moves, ship it, and defend it. Vocab: Voice. The eight-part
            capstone format like 3.5 and 4.5.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 5.4 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 5.3',
    'Receipt',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
